import {
  GetLocationResponseDto,
  KitchenAddressResponseDto,
} from "types/locations";
import { boolean, number, object, string } from "yup";

const NUMBER = "(-?\\d{1,3}(?:\\.\\d+)?)";

// Google Maps pin in a place link: ".../data=!3d29.378586!4d47.990341"
const GOOGLE_MAPS_PIN = new RegExp(`!3d${NUMBER}!4d${NUMBER}`);
// Map center or query in a link: "/@29.378586,47.990341,17z" or "?q=29.378586,47.990341"
const GOOGLE_MAPS_CENTER = new RegExp(`[@=]${NUMBER},\\s*${NUMBER}`);
// Plain pair copied from Google Maps: "29.378586, 47.990341"
const PLAIN_PAIR = new RegExp(
  `^\\(?\\s*${NUMBER}\\s*[,\\s]\\s*${NUMBER}\\s*\\)?$`,
);

export const parseKitchenCoordinates = (
  value?: string | null,
): KitchenAddressResponseDto | null => {
  const raw = value?.trim();

  if (!raw) {
    return null;
  }

  const match =
    raw.match(GOOGLE_MAPS_PIN) ??
    raw.match(GOOGLE_MAPS_CENTER) ??
    raw.match(PLAIN_PAIR);

  if (!match) {
    return null;
  }

  const lat = Number(match[1]);
  const lng = Number(match[2]);

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  return { lat, lng };
};

export const formatKitchenCoordinates = (
  kitchenAddress?: KitchenAddressResponseDto | null,
) => (kitchenAddress ? `${kitchenAddress.lat}, ${kitchenAddress.lng}` : "");

export const getValidation = (data: GetLocationResponseDto) => {
  if (!data) {
    return undefined;
  }
  const { operatingZones } = data || {};

  const zonesValidation = operatingZones.reduce((acc, _el, i) => {
    acc[`zone${i}`] = object().shape({
      name: string().trim().required("Name is required"),
      priority: number().required("Priority is required"),
    });
    return acc;
  }, {});

  const schema = object().shape({
    name: string().trim().required("Name is required"),
    zones: object().shape(zonesValidation),
    pickupEnabled: boolean(),
    deliveryEnabled: boolean(),
    pickupHoursFrom: string().when("pickupEnabled", {
      is: true,
      then: schema => schema.required("Pickup start time is required"),
    }),
    pickupHoursTo: string().when("pickupEnabled", {
      is: true,
      then: schema => schema.required("Pickup end time is required"),
    }),
    pickupAddressCity: string().when("pickupEnabled", {
      is: true,
      then: schema => schema.required("City is required"),
    }),
    pickupAddressArea: string().when("pickupEnabled", {
      is: true,
      then: schema => schema.required("Area is required"),
    }),
    pickupAddressBlock: string().when("pickupEnabled", {
      is: true,
      then: schema => schema.required("Block no. is required"),
    }),
    pickupAddressStreet: string().when("pickupEnabled", {
      is: true,
      then: schema => schema.required("Street is required"),
    }),
    pickupAddressBuilding: string().when("pickupEnabled", {
      is: true,
      then: schema => schema.required("Building/Apartment no. is required"),
    }),
    kitchenCoordinates: string().test(
      "kitchen-coordinates",
      "Paste coordinates like 29.378586, 47.990341 or a Google Maps link",
      value => !value?.trim() || !!parseKitchenCoordinates(value),
    ),
    notificationWhatsappNumber: string()
      .trim()
      .matches(/^\+?[1-9]\d{6,14}$/, {
        message: "Enter a valid phone number, e.g. +96550123456",
        excludeEmptyString: true,
      }),
  });

  return schema;
};
